import _ from 'lodash'

import {
  BrandSubscriptionPaymentFrequency,
  BrandSubscriptionPlatform,
  brandSubscriptionPlatformsPricedPerItem,
  BrandSubscriptionTier,
} from '@/utils/brand-subscription/constants'
import {Currency} from '@/utils/currency'
import {
  BrandSubscriptionFragment,
  BrandSubscriptionPlatformFragment,
  DiscountCouponFragment,
} from '@/utils/graphqlSdk'
import {
  BasePrice,
  billingEntities,
  BillingEntityId,
  calculateVatStatus,
  createDiscountCouponItems,
  createFeeItem,
  MonitoringPriceSheetKey,
  sumFees,
  sumPrices,
} from '@/utils/pricing'
import {
  BrandSubscriptionPaymentPrice,
  BrandSubscriptionPaymentPriceFee,
  BrandSubscriptionPaymentPricePlatformFee,
} from '@/utils/pricing/brand-subscription-payment/types'

import {prices} from '../prices'

const createBrandSubscriptionFeeItem = (
  descKey: string,
  item: string,
  unitPrice: number,
  qty: number,
  vatRate: number,
  isGovernment: boolean,
): BrandSubscriptionPaymentPriceFee => ({
  ...createFeeItem(descKey, unitPrice, qty, vatRate, isGovernment),
  item,
})

export const getPlatformItemPrice = (
  paymentFrequency: BrandSubscriptionPaymentFrequency,
  brandSubscriptionPlatformName: BrandSubscriptionPlatform,
  currency: Currency,
): number => {
  const priceSheetItemName =
    `${brandSubscriptionPlatformName}-${paymentFrequency}` as MonitoringPriceSheetKey
  return prices.monitoring[priceSheetItemName][currency]
}

const calculatePlatformFees = (
  paymentFrequency: BrandSubscriptionPaymentFrequency,
  brandSubscriptionPlatform: Pick<BrandSubscriptionPlatformFragment, 'name' | 'metadata'>,
  currency: Currency,
  vatRate: number,
): BrandSubscriptionPaymentPricePlatformFee => {
  const numberOfMonths = paymentFrequency === BrandSubscriptionPaymentFrequency.yearly ? 12 : 1
  const items = brandSubscriptionPlatform.metadata?.items ?? []

  const platformBasePrice = getPlatformItemPrice(
    paymentFrequency,
    brandSubscriptionPlatform.name as BrandSubscriptionPlatform,
    currency,
  )

  const fees = brandSubscriptionPlatformsPricedPerItem.includes(brandSubscriptionPlatform.name)
    ? items.map((item: string) =>
        createBrandSubscriptionFeeItem(
          brandSubscriptionPlatform.name,
          item,
          platformBasePrice,
          numberOfMonths,
          vatRate,
          false,
        ),
      )
    : [
        createBrandSubscriptionFeeItem(
          brandSubscriptionPlatform.name,
          brandSubscriptionPlatform.name,
          platformBasePrice,
          numberOfMonths,
          vatRate,
          false,
        ),
      ]

  return {
    platform: brandSubscriptionPlatform.name as BrandSubscriptionPlatform,
    fees,
  }
}

export const getTierBasePrice = (
  paymentFrequency: BrandSubscriptionPaymentFrequency,
  tier: BrandSubscriptionTier,
  currency: Currency,
): number => {
  const normalizedTier = tier.replace('v2-', '')
  const priceSheetItemName = `${normalizedTier}-${paymentFrequency}` as MonitoringPriceSheetKey
  return prices.monitoring[priceSheetItemName][currency]
}

const calculateTierFee = (
  paymentFrequency: BrandSubscriptionPaymentFrequency,
  tier: BrandSubscriptionTier,
  currency: Currency,
  vatRate: number,
): {tierFee: BrandSubscriptionPaymentPriceFee; subtotal: BasePrice} => {
  const numberOfMonths = paymentFrequency === BrandSubscriptionPaymentFrequency.yearly ? 12 : 1
  const basePrice = getTierBasePrice(paymentFrequency, tier, currency)

  const tierFee = createBrandSubscriptionFeeItem(
    tier,
    tier,
    basePrice,
    numberOfMonths,
    vatRate,
    false,
  )

  return {
    tierFee,
    subtotal: tierFee.total,
  }
}

const calculatePlatformsFees = (
  paymentFrequency: BrandSubscriptionPaymentFrequency,
  platforms: Pick<BrandSubscriptionPlatformFragment, 'name' | 'metadata'>[],
  currency: Currency,
  vatRate: number,
): {platformFees: BrandSubscriptionPaymentPricePlatformFee[]; subtotal: BasePrice} => {
  const platformFees = platforms.map((platform) =>
    calculatePlatformFees(paymentFrequency, platform, currency, vatRate),
  )

  return {
    platformFees,
    subtotal: sumFees(_.flatMap(platformFees, 'fees')),
  }
}

export const calculateBrandSubscriptionPrice = (
  brandSubscription: Pick<BrandSubscriptionFragment, 'payment_frequency' | 'tier'>,
  brandSubscriptionPlatforms: Pick<BrandSubscriptionPlatformFragment, 'name' | 'metadata'>[],
  billedEntity:
    | {
        type?: string | null
        address?: {country?: string | null} | null
      }
    | null
    | undefined,
  currency: Currency,
  forceVatIncluded: boolean,
  discountCoupon?: DiscountCouponFragment | null,
  billingEntityId: BillingEntityId = 'SK',
): BrandSubscriptionPaymentPrice => {
  const billingEntity = billingEntities[billingEntityId]

  const {vatIncluded, reverseVatCharge} = calculateVatStatus(
    billingEntity,
    billedEntity,
    forceVatIncluded,
  )

  const vatRate = forceVatIncluded || vatIncluded ? billingEntity.vatRate : 0
  const paymentFrequency = brandSubscription.payment_frequency as BrandSubscriptionPaymentFrequency
  const tier = brandSubscription.tier as BrandSubscriptionTier
  const feesAndSubtotal = brandSubscription.tier.startsWith('v2')
    ? calculateTierFee(paymentFrequency, tier, currency, vatRate)
    : calculatePlatformsFees(paymentFrequency, brandSubscriptionPlatforms, currency, vatRate)

  const couponDiscounts = discountCoupon
    ? createDiscountCouponItems(feesAndSubtotal.subtotal.vatBase, 0, discountCoupon, vatRate)
    : []
  const couponDiscountsTotal = sumFees(couponDiscounts)

  const total = sumPrices([feesAndSubtotal.subtotal, couponDiscountsTotal])

  return {
    ...feesAndSubtotal,
    total,
    couponDiscounts,
    couponDiscountsTotal,
    currency,
    vatIncluded,
    reverseVatCharge,
    vatRate,
  }
}
